// ホームのグラフの計算（ADR 0034）。画面から切り離してテストする（home-viz.test.mjs）。
// 見た目は Lucian UX/UI Prompt の部品を移植したもの（components/lucian/）。ここは数字だけを扱う。
import { acqDenominator } from './seat-rate.mjs';

// ---- ファネル（#160 Conversion funnel） ----
export const FUNNEL_STAGES = [
  { key: 'catch',    label: 'キャッチ' },
  { key: 'sticker',  label: 'ティッシュ回収' },
  { key: 'talk',     label: 'トーク開始' },
  { key: 'fulltalk', label: 'フルトーク' },
  { key: 'seat',     label: '着座' },
  { key: 'acq',      label: '獲得' },
];

// 件数がごく少ない段でも、押せて名前と数字が読める幅を残す。
export const FUNNEL_MIN_WIDTH = 0.18;

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const ratio = (num, den) => (den > 0 ? num / den : null);

/**
 * 段ごとの件数・幅・率を出す。
 * fromPrev = 前の段からの率（獲得の段は有効着座が分母。ADR 0028）
 * toAcq    = その段から獲得までの率（着座の段は有効着座が分母。獲得の段は null）
 * width    = いちばん多い段に対する割合（0〜1。最小 FUNNEL_MIN_WIDTH）
 */
export function buildFunnel(metrics) {
  const m = metrics || {};
  const counts = FUNNEL_STAGES.map(s => n(m[s.key]));
  const max = Math.max(...counts, 0);
  const validSeat = acqDenominator({ seat: n(m.seat), valid_seat: m.valid_seat });
  const acq = n(m.acq);
  return FUNNEL_STAGES.map((s, i) => {
    const count = counts[i];
    const prevDen = i === 0 ? 0 : (s.key === 'acq' ? validSeat : counts[i - 1]);
    const acqDen = s.key === 'seat' ? validSeat : count;
    return {
      ...s,
      count,
      width: max > 0 ? Math.max(FUNNEL_MIN_WIDTH, count / max) : FUNNEL_MIN_WIDTH,
      fromPrev: i === 0 ? null : ratio(count, prevDen),
      toAcq: s.key === 'acq' ? null : ratio(acq, acqDen),
    };
  });
}

// ---- ドーナツ（#097 Donut that re-splits） ----
/**
 * 件数の多い順に keep 個まで残し、残りは「その他」に足す。0件は落とす。
 * slot は色の番号（残した項目だけ 0..keep-1、「その他」は null＝中立色）。
 * 暗い面で全ペアの見分けが通るのは3色まで（dataviz の検証、ADR 0027）なので既定は3。
 */
export function foldDonut(rows, keep = 3) {
  const list = (rows || []).filter(r => n(r.n) > 0).sort((a, b) => n(b.n) - n(a.n));
  const head = list.slice(0, keep).map((r, i) => ({ label: r.label, n: n(r.n), slot: i, other: false }));
  const rest = list.slice(keep).reduce((s, r) => s + n(r.n), 0);
  return rest > 0 ? [...head, { label: 'その他', n: rest, slot: null, other: true }] : head;
}

// ---- 推移の線（#098 の下の線） ----
/** 値の並びを w×h の枠（上下左右 pad の余白）に収めた点にする。2点未満は空。 */
export function sparkPoints(values, w, h, pad = 2) {
  const v = (values || []).map(n);
  if (v.length < 2) return [];
  const min = Math.min(...v), max = Math.max(...v);
  const step = (w - pad * 2) / (v.length - 1);
  return v.map((val, i) => ({
    x: pad + step * i,
    y: max === min ? h / 2 : pad + (1 - (val - min) / (max - min)) * (h - pad * 2),
    value: val,
  }));
}

// ---- 回る数字（#098 Live metric with rolling digits） ----
/** 表示する文字の並び（3桁区切り込み）。数字の桁だけが回る。 */
export function digitsOf(value) {
  return Math.round(n(value)).toLocaleString('en-US').split('');
}

// ---- 読み込みの再試行（#090 Error widget that plugs back in） ----
// 自動の再試行は最大3回。失敗のたびに間を広げる（ダッシュボード API は1回で数千行を読むため、
// 連打で D1 の読み取りを増やさない。rules/lessons.md）。それ以降は「今すぐ再試行」だけ。
export const MAX_AUTO_RETRY = 3;
const RETRY_DELAYS = [5000, 15000, 30000];
export function retryDelayMs(attempt) {
  return attempt >= 0 && attempt < MAX_AUTO_RETRY ? RETRY_DELAYS[attempt] : null;
}

// ---- ブレットチャート（#163 Bullet chart target） ----
/** 目盛りの上限（0〜1）。いちばん大きい値に 25% の余白を足し、0.1 刻みで切り上げる。最大 1。 */
export function bulletScale(values) {
  const max = Math.max(...(values || []).map(n), 0);
  if (max <= 0) return 0.1;
  return Math.min(1, Math.ceil(max * 1.25 * 10 - 1e-9) / 10);
}
